import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as CartActions from '../../Redux/Cart/CartActions';
import SystemDataService from "../../Services/SystemDataService.js";
import { resizeImageToBase64 } from "../../Services/UtilsService.js";

import Selector from '../../Utils/Select/select-component.js';

// Layout & Reused Components
import Layout from "../../Utils/Navbar/navbar-layout-component.js";
import Footer from "../../Utils/Footer/footer-component";
import { Modal } from "../../Utils/Modal/modal-component";
import { LoadingAnimation } from "../../Utils/Progress/progress-component";
import Toast from "../../Utils/Toast/toast-component.js";
import SkeletonBuilder from "../../Utils/Skeleton/skeleton-builder-component";
import { ButtonPrimary, ButtonWhite, ButtonBlack, ButtonError, ToggleButton } from "../../Utils/Buttons/button-component.js";
import { InputComponent, TextAreaComponent, FileInput } from "../../Utils/Inputs/input-component.js";
import Image from "../../Utils/Image/image-component.js";
import AssetPickerModal from "../../Utils/Modal/modal-assets-picker";
import EntryCard from "../../Utils/Cards/card-entiry-component"; // STRICTLY REUSING YOUR COMPONENT

// --- ZOHO-ALIGNED DEFAULT PRODUCT ---
const defaultProduct = {
    handle: "", sku: "", name: "", brand: "", 
    image: "", previewImage: "", images: [],
    pricing: { cost: 0, sellingPrice: 0, currency: "USD" },
    inventory: { itemType: "inventory", stockOnHand: 0, reorderLevel: 0, weight: "", status: "ACTIVE" },
    category: { name: "", url: "" },
    description: "", longDescription: "",
    specifications: {},
    seo: { title: "", description: "", keyword: "" },
    tags: [], showInStore: true
};

// ==========================================
// 1. HEADER BAR
// ==========================================
const HeaderBar = ({ onAddNew, itemCount, onBack, dir }) => (
    <div className="w-full flex flex-col md:flex-row justify-between items-end mb-12 border-b border-neutral-100 dark:border-neutral-800 pb-8 gap-6">
        <div className="flex flex-col gap-2">
            <button onClick={onBack} className="flex items-center gap-2 text-md font-bold text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors uppercase mb-2">
                {dir === 'rtl' ? '←' : '→'} Back to Dashboard
            </button>
            <div className="flex items-center gap-4">
                <h1 className="text-4xl font-black text-neutral-900 dark:text-white  capitalize">
                    Product Catalog
                </h1>
                <span className="px-3 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800 text-[10px] font-bold text-neutral-500 uppercase ">
                    {itemCount} Items
                </span>
            </div>
            <p className="text-neutral-500 font-medium text-sm max-w-md">
                Manage inventory, pricing, media, and SEO data aligned with Zoho.
            </p>
        </div>
        <div className="flex items-center gap-3">
            <ButtonBlack onClick={onAddNew} className="!px-8 !py-3 !rounded-xl !text-xs font-bold shadow-xl active:scale-95 transition-all">
                + Add New Product
            </ButtonBlack>
        </div>
    </div>
);

// ==========================================
// 2. EXTRACTED MEDIA SELECTOR
// ==========================================
const MediaSelectorBlock = ({ label, value, onChange, onBrowse }) => {
    const [uploading, setUploading] = useState(false);
    const hasValue = !!value;
    const isVideo = hasValue && value.toLowerCase().match(/\.(mp4|webm|mov|ogg)$/);

    return (
        <div className="flex flex-col gap-4 w-full p-5 bg-[#fafafa] dark:bg-neutral-900/50 border border-neutral-100 dark:border-neutral-800 rounded-2xl shadow-sm">
            <label className="text-[11px] font-bold text-neutral-500 uppercase tracking-widest">{label}</label>
            <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-white dark:bg-black border border-neutral-200 dark:border-neutral-800 flex items-center justify-center">
                {hasValue ? (
                    <>
                        {isVideo ? <video src={value} className="w-full h-full object-contain" controls /> : <Image src={value} className="w-full h-full object-contain p-2" />}
                        <button onClick={() => onChange("")} className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-red-500 text-white rounded-md transition-all shadow-lg backdrop-blur-sm">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </>
                ) : <span className="text-neutral-300 dark:text-neutral-700 text-xs font-bold uppercase tracking-widest">No Media</span>}
            </div>
            <div className="flex flex-col gap-2">
                <FileInput 
                    label={uploading ? "Uploading to S3..." : "Upload File"} 
                    className="!w-full !py-3 bg-white dark:bg-black shadow-sm" 
                    onFileSelected={async (file) => {
                        setUploading(true);
                        try {
                            const url = await SystemDataService.uploadMedia(file);
                            if (url) onChange(url);
                        } finally { setUploading(false); }
                    }} 
                />
                <div className="flex items-center gap-2 px-2">
                    <div className="h-px bg-neutral-200 dark:bg-neutral-800 flex-grow" />
                    <span className="text-[10px] font-bold text-neutral-400 uppercase">OR</span>
                    <div className="h-px bg-neutral-200 dark:bg-neutral-800 flex-grow" />
                </div>
                <ButtonBlack onClick={onBrowse} className="!w-full !py-3 !text-[12px] font-bold !rounded-xl shadow-sm">
                    Browse Library
                </ButtonBlack>
            </div>
        </div>
    );
};

// ==========================================
// 3. MAIN SCREEN COMPONENT
// ==========================================
function ProductEditor() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const systemData = useSelector((state) => state.systemData || {});
    const { dir, categories: siteCategories = [] } = systemData;
    const products = useSelector((state) => Array.isArray(state.cart?.products) ? state.cart.products : []);
    const loading = useSelector((state) => state.loading);

    const [draft, setDraft] = useState(defaultProduct);
    const [openModal, setOpenModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [skeletonLoading, setSkeletonLoading] = useState(true);
    
    // UI States for Modal
    const [activeTab, setActiveTab] = useState('general');
    const [mobileView, setMobileView] = useState('menu');
    const [pickerOpen, setPickerOpen] = useState(false);
    const [assetTarget, setAssetTarget] = useState(null); 
    const [uploadingGallery, setUploadingGallery] = useState(false);

    const isRtl = dir === 'rtl';

    useEffect(() => {
        setSkeletonLoading(true);
        const timeout = setTimeout(() => setSkeletonLoading(false), 600);
        return () => clearTimeout(timeout);
    }, [products]);

    // Handlers
    const handleNestedChange = (section, field, value) => {
        setDraft(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
    };

    const handleSave = () => {
        if (!draft.name || !draft.sku) return alert("Product Name and SKU are required.");
        
        const payload = { ...draft };
        if (editingId) {
            dispatch(CartActions.editProduct({ ...payload, id: editingId }));
        } else {
            payload.id = Math.random().toString(36).substr(2, 9);
            dispatch(CartActions.addProduct(payload));
        }
        setOpenModal(false);
    };

    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this product?")) {
            dispatch(CartActions.removeProduct(id));
        }
    };

    const handleClone = (product) => {
        const cloned = JSON.parse(JSON.stringify(product));
        cloned.id = Math.random().toString(36).substr(2, 9);
        cloned.name = `${cloned.name} (Copy)`;
        cloned.sku = `${cloned.sku}-COPY`;
        cloned.showInStore = false; 
        dispatch(CartActions.addProduct(cloned));
    };

    const openAssetPicker = (target) => {
        setAssetTarget(target);
        setPickerOpen(true);
    };

    const handleAssetSelect = (url) => {
        if (assetTarget === 'main') setDraft(prev => ({ ...prev, image: url }));
        else if (assetTarget === 'preview') setDraft(prev => ({ ...prev, previewImage: url }));
        else if (assetTarget === 'gallery') setDraft(prev => ({ ...prev, images: [...prev.images, url] }));
    };

    const tabs = [
        { id: 'general', label: 'General Info' },
        { id: 'media', label: 'Media & Images' },
        { id: 'pricing', label: 'Pricing & Inventory' },
        { id: 'advanced', label: 'SEO & Metadata' }
    ];

    // Dummy strings for EntryCard fallbacks
    const cardStrings = { card: { itemsCount: "Items", delete: "Delete" }, alerts: { deleteConfirm: "Are you sure?" } };

    return (
        <div className="bg-neutral-50 dark:bg-[#080808] min-h-screen font-assistant selection:bg-blue-100 dark:selection:bg-blue-900/30">
            <Toast />
            <Modal expand={loading.visible} timeout backdrop='bg-white/80 backdrop-blur-sm' popUp>
                <LoadingAnimation loadingMessage={loading.message} />
            </Modal>

            <Layout>
                <main className="relative mx-auto w-full max-w-7xl px-4 md:px-6 py-12 min-h-screen" dir={dir}>
                    <HeaderBar 
                        dir={dir} 
                        itemCount={products.length} 
                        onBack={() => navigate('/home')} 
                        onAddNew={() => {
                            setEditingId(null);
                            setDraft(JSON.parse(JSON.stringify(defaultProduct)));
                            setActiveTab('general');
                            setMobileView('menu');
                            setOpenModal(true);
                        }} 
                    />

                    {products.length === 0 && !skeletonLoading ? (
                        <div className="w-full py-24 text-center border-2 border-dashed border-neutral-200 dark:border-neutral-800 rounded-3xl">
                            <p className="text-neutral-400 font-bold uppercase tracking-widest">No Products Found</p>
                            <p className="text-sm text-neutral-500 mt-2">Click "+ Add New Product" to create your first inventory item.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                            {skeletonLoading ? (
                                Array.from({ length: 4 }).map((_, i) => <SkeletonBuilder key={i} />)
                            ) : (
                                products.map((product) => {
                                    // Flatten data for EntryCard so nested objects don't render as "{...}"
                                    const displayEntry = {
                                        id: product.id,
                                        name: product.name || 'Untitled',
                                        image: product.image,
                                        isActive: product.showInStore,
                                        sku: product.sku || 'N/A',
                                        price: `$${product.pricing?.sellingPrice || '0.00'}`,
                                        cost: `$${product.pricing?.cost || '0.00'}`,
                                        stock: `${product.inventory?.stockOnHand || 0} Units`,
                                        category: product.category?.name || '—',
                                    };

                                    return (
                                        <EntryCard
                                            key={product.id}
                                            entry={displayEntry}
                                            primaryImageKey="image"
                                            strings={cardStrings}
                                            disableDelete={false}
                                            onEdit={() => {
                                                // We pass the ORIGINAL product back to state, not the flat displayEntry
                                                setDraft(JSON.parse(JSON.stringify(product)));
                                                setEditingId(product.id);
                                                setActiveTab('general');
                                                setMobileView('menu');
                                                setOpenModal(true);
                                            }}
                                            onClone={() => handleClone(product)}
                                            onDelete={() => handleDelete(product.id)}
                                        />
                                    );
                                })
                            )}
                        </div>
                    )}
                </main>
            </Layout>
            <Footer />

            {/* --- EDITOR MODAL --- */}
            <Modal expand={openModal}>
                <div dir={dir} className="w-full lg:max-w-[85vw] bg-white dark:bg-[#0c0c0c] rounded-2xl shadow-2xl flex flex-col h-[90vh] lg:h-[85vh] border border-neutral-200 dark:border-neutral-800 overflow-hidden font-primary">
                    
                    <div className="px-8 py-5 border-b border-neutral-100 dark:border-neutral-800 flex justify-between items-center bg-white dark:bg-[#0c0c0c] shrink-0 z-20">
                        <div className="flex items-center gap-4">
                            {mobileView === 'content' && (
                                <button onClick={() => setMobileView('menu')} className="lg:hidden p-2 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
                                    <svg className={`w-4 h-4 ${dir === 'rtl' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7" /></svg>
                                </button>
                            )}
                            <h2 className="text-xl font-bold text-neutral-900 dark:text-white ">
                                {mobileView === 'menu' ? (editingId ? 'Edit Product' : 'New Product') : tabs.find(t => t.id === activeTab)?.label}
                            </h2>
                        </div>
                        <button onClick={() => setOpenModal(false)} className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors">
                            <svg className="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>

                    <div className="flex flex-grow overflow-hidden relative">
                        {/* SIDEBAR TABS */}
                        <div className={`${mobileView === 'menu' ? 'flex' : 'hidden lg:flex'} absolute inset-0 z-10 lg:relative lg:inset-auto w-full lg:w-72 border-r border-neutral-100 dark:border-neutral-800 bg-[#fcfcfc] dark:bg-[#0a0a0a] flex flex-col p-5 shrink-0 overflow-y-auto`}>
                            <span className="text-[12px] font-bold text-neutral-400 mb-4 block px-3 uppercase tracking-wider">Navigation</span>
                            <div className="space-y-2">
                                {tabs.map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => { setActiveTab(tab.id); setMobileView('content'); }}
                                        className={`w-full px-5 py-3.5 rounded-xl text-left text-[14px] font-bold transition-all ${activeTab === tab.id ? 'bg-blue-600 text-white shadow-lg' : 'text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-900'}`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* MAIN WORKSPACE */}
                        <div className="flex-grow overflow-y-auto bg-white dark:bg-[#0c0c0c] custom-scrollbar pb-32">
                            <div className="max-w-4xl mx-auto py-10 px-6 lg:px-12 text-start">
                                
                                {activeTab === 'general' && (
                                    <div className="space-y-8 animate-fadeIn">
                                        <div className="flex items-center justify-between p-5 bg-[#fafafa] dark:bg-[#111] rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm">
                                            <div>
                                                <span className="font-bold dark:text-white text-md block">Product Visibility</span>
                                                <span className="text-xs text-neutral-500">Show or hide this product from the storefront.</span>
                                            </div>
                                            <ToggleButton isOn={draft.showInStore} onToggle={next => setDraft({ ...draft, showInStore: next })} className="scale-110" />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">Product Name</label>
                                                <InputComponent placeholder="Product Name" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} className="!rounded-xl h-12" />
                                            </div>
                                            <div>
                                                <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">SKU (Zoho Sync)</label>
                                                <InputComponent placeholder="SKU" value={draft.sku} onChange={e => setDraft({ ...draft, sku: e.target.value })} className="!rounded-xl h-12" />
                                            </div>
                                            <div>
                                                <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">Brand</label>
                                                <InputComponent placeholder="Brand" value={draft.brand} onChange={e => setDraft({ ...draft, brand: e.target.value })} className="!rounded-xl h-12" />
                                            </div>
                                            <div>
                                                <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">Status</label>
                                                <Selector
                                                    options={['ACTIVE', 'PAUSED', 'DISABLED']}
                                                    value={draft.status || 'ACTIVE'}
                                                    onChange={(val) => setDraft({ ...draft, status: val })}
                                                    placeholder="Select Status"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">Category</label>
                                                <Selector
                                                    options={siteCategories.map(c => ({ id: c.id, title: c.name, label: c.name }))}
                                                    value={draft.category?.name || ''}
                                                    onChange={(val) => {
                                                        const matched = siteCategories.find(c => c.name === val);
                                                        setDraft(prev => ({
                                                            ...prev,
                                                            category: { id: matched?.id || '', name: val, url: matched?.slug || '' },
                                                            categoryIds: matched?.id ? [matched.id] : prev.categoryIds,
                                                        }));
                                                    }}
                                                    placeholder="Select Category"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">URL Handle</label>
                                                <InputComponent placeholder="url-handle-slug" value={draft.handle} onChange={e => setDraft({ ...draft, handle: e.target.value })} className="!rounded-xl h-12" />
                                            </div>
                                            <div>
                                                <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">Tags (Comma Separated)</label>
                                                <InputComponent placeholder="Tag1, Tag2" value={draft.tags?.join(', ') || ''} onChange={e => setDraft({ ...draft, tags: e.target.value.split(',').map(t => t.trim()) })} className="!rounded-xl h-12" />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">Short Description</label>
                                            <TextAreaComponent rows={2} placeholder="Short subtitle..." value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} className="!rounded-xl" />
                                        </div>
                                        <div>
                                            <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">Long Description</label>
                                            <TextAreaComponent rows={6} placeholder="Full details..." value={draft.longDescription} onChange={e => setDraft({ ...draft, longDescription: e.target.value })} className="!rounded-xl" />
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'media' && (
                                    <div className="space-y-8 animate-fadeIn">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                            <MediaSelectorBlock 
                                                label="Main Product Image" 
                                                value={draft.image} 
                                                targetField="image" 
                                                onChange={(val) => setDraft(prev => ({...prev, image: val}))}
                                                onBrowse={() => openAssetPicker('main')}
                                            />
                                            <MediaSelectorBlock 
                                                label="Hover / Preview Image" 
                                                value={draft.previewImage} 
                                                targetField="previewImage" 
                                                onChange={(val) => setDraft(prev => ({...prev, previewImage: val}))}
                                                onBrowse={() => openAssetPicker('preview')}
                                            />
                                        </div>
                                        
                                        <div className="pt-8 border-t border-neutral-100 dark:border-neutral-800">
                                            <label className="text-[12px] font-bold text-neutral-500 block mb-4 ml-1">Gallery Images</label>
                                            
                                            {draft.images.length > 0 && (
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                                    {draft.images.map((img, idx) => (
                                                        <div key={idx} className="relative group aspect-square rounded-xl overflow-hidden border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-[#111] shadow-sm">
                                                            <Image src={img} wrapperClassName="absolute inset-0 w-full h-full" className="w-full h-full object-cover" />
                                                            <button onClick={() => setDraft({...draft, images: draft.images.filter((_, i) => i !== idx)})} className="absolute top-2 right-2 bg-red-500 text-white rounded-md p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
                                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" /></svg>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="flex flex-col md:flex-row items-center gap-3 bg-[#fafafa] dark:bg-neutral-900/50 p-4 border border-neutral-100 dark:border-neutral-800 rounded-2xl shadow-sm">
                                                <FileInput 
                                                    label={uploadingGallery ? "Uploading to S3..." : "Upload New Image to Gallery"} 
                                                    className="!w-full md:!w-1/2 !py-3 bg-white dark:bg-black shadow-sm" 
                                                    onFileSelected={async (file) => {
                                                        setUploadingGallery(true);
                                                        try {
                                                            const url = await SystemDataService.uploadMedia(file);
                                                            if (url) setDraft(prev => ({ ...prev, images: [...prev.images, url] }));
                                                        } finally { 
                                                            setUploadingGallery(false); 
                                                        }
                                                    }} 
                                                />
                                                <div className="flex items-center gap-2 px-2 w-full md:w-auto">
                                                    <div className="h-px bg-neutral-200 dark:bg-neutral-800 flex-grow md:hidden" />
                                                    <span className="text-[10px] font-bold text-neutral-400 uppercase">OR</span>
                                                    <div className="h-px bg-neutral-200 dark:bg-neutral-800 flex-grow md:hidden" />
                                                </div>
                                                <ButtonBlack onClick={() => openAssetPicker('gallery')} className="!w-full md:!w-auto !py-3 !px-6 !text-[12px] font-bold !rounded-xl shadow-sm whitespace-nowrap">
                                                    Select from Library
                                                </ButtonBlack>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'pricing' && (
                                    <div className="space-y-10 animate-fadeIn">
                                        <div>
                                            <h3 className="text-lg font-black dark:text-white mb-6 border-b border-neutral-100 dark:border-neutral-800 pb-2">Pricing</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">Selling Price ($)</label>
                                                    <InputComponent type="number" value={draft.pricing.sellingPrice} onChange={e => handleNestedChange('pricing', 'sellingPrice', parseFloat(e.target.value))} className="!rounded-xl h-12" />
                                                </div>
                                                <div>
                                                    <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">Our Cost ($)</label>
                                                    <InputComponent type="number" value={draft.pricing.cost} onChange={e => handleNestedChange('pricing', 'cost', parseFloat(e.target.value))} className="!rounded-xl h-12" />
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <h3 className="text-lg font-black dark:text-white mb-6 border-b border-neutral-100 dark:border-neutral-800 pb-2">Inventory Sync</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">Stock On Hand</label>
                                                    <InputComponent type="number" value={draft.inventory.stockOnHand} onChange={e => handleNestedChange('inventory', 'stockOnHand', parseInt(e.target.value))} className="!rounded-xl h-12" />
                                                </div>
                                                <div>
                                                    <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">Reorder Level</label>
                                                    <InputComponent type="number" value={draft.inventory.reorderLevel} onChange={e => handleNestedChange('inventory', 'reorderLevel', parseInt(e.target.value))} className="!rounded-xl h-12" />
                                                </div>
                                                <div>
                                                    <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">Weight</label>
                                                    <InputComponent placeholder="e.g. 1.5 lb" value={draft.inventory.weight} onChange={e => handleNestedChange('inventory', 'weight', e.target.value)} className="!rounded-xl h-12" />
                                                </div>
                                                <div>
                                                    <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">Zoho Item Type</label>
                                                    <InputComponent value={draft.inventory.itemType} disabled className="!rounded-xl h-12 opacity-50 cursor-not-allowed" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'advanced' && (
                                    <div className="space-y-10 animate-fadeIn">
                                        <div>
                                            <h3 className="text-lg font-black dark:text-white mb-6 border-b border-neutral-100 dark:border-neutral-800 pb-2">SEO Meta Data</h3>
                                            <div className="space-y-6">
                                                <div>
                                                    <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">Meta Title</label>
                                                    <InputComponent placeholder="SEO Title" value={draft.seo.title} onChange={e => handleNestedChange('seo', 'title', e.target.value)} className="!rounded-xl h-12" />
                                                </div>
                                                <div>
                                                    <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">Meta Description</label>
                                                    <TextAreaComponent rows={2} placeholder="SEO Description" value={draft.seo.description} onChange={e => handleNestedChange('seo', 'description', e.target.value)} className="!rounded-xl" />
                                                </div>
                                                <div>
                                                    <label className="text-[12px] font-bold text-neutral-500 block mb-2 ml-1">Focus Keyword</label>
                                                    <InputComponent placeholder="Target Keyword" value={draft.seo.keyword} onChange={e => handleNestedChange('seo', 'keyword', e.target.value)} className="!rounded-xl h-12" />
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <div className="flex justify-between items-end mb-2 ml-1">
                                                <label className="text-[12px] font-bold text-neutral-500">Specifications (JSON)</label>
                                                <span className="text-[10px] font-bold text-primary-500 bg-primary-500/10 px-2 py-1 rounded-md">Maps to Zoho Custom Fields</span>
                                            </div>
                                            <TextAreaComponent 
                                                rows={5} dir="ltr" className="!rounded-xl font-mono text-[13px] bg-[#1e1e1e] text-[#9cdcfe]"
                                                value={JSON.stringify(draft.specifications, null, 2)} 
                                                onChange={e => { try { setDraft({ ...draft, specifications: JSON.parse(e.target.value) }); } catch (err) {} }} 
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* MODAL FOOTER */}
                    <div className="absolute bottom-4 left-4 right-4 sm:mx-auto sm:max-w-md flex items-center justify-center gap-4 px-8 py-5 z-30 bg-white/95 dark:bg-[#0c0c0c]/95 backdrop-blur-md border border-neutral-100 dark:border-neutral-800 shadow-2xl rounded-3xl lg:static lg:bottom-auto lg:left-auto lg:right-auto lg:max-w-xl lg:w-full lg:rounded-none lg:border-t lg:border-b-0 lg:border-x-0 lg:justify-end lg:px-10 lg:py-6 lg:shadow-none lg:bg-white lg:dark:bg-[#0c0c0c] transition-all duration-300">
                        <ButtonError onClick={() => setOpenModal(false)}>Cancel</ButtonError>
                        <ButtonBlack onClick={handleSave}>{editingId ? 'Save Changes' : 'Create Product'}</ButtonBlack>
                    </div>

                </div>
            </Modal>

            {/* --- ASSET PICKER MODAL (PLACED LAST TO ENSURE IT SITS ON TOP OF THE EDITOR MODAL) --- */}
            <AssetPickerModal 
                open={pickerOpen} 
                onClose={() => setPickerOpen(false)} 
                onSelect={handleAssetSelect} 
                dir={dir} 
                strings={{ 
                    gallery: { title: "Media Library", itemsCount: "Items", subtitle: "Select media for your product", selectImage: "Select", noAssets: "No media found." }, 
                    card: { itemsCount: "Items" }, 
                    modal: { cancel: "Cancel" } 
                }} 
            />
        </div>
    );
}

export default ProductEditor;